import {
  useState,
  useEffect,
  useMemo,
  type PropsWithChildren,
  useCallback,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { mapValues } from "lodash-es";
import {
  TravellersSelectionContext,
  type TravellerSlots,
  type TravellersSelectionContextValue,
} from "./TravellersSelectionContext";
import { getTravellerTypeAtTravelDate } from "../../utils/ageValidation";
import { travelerTypeToTypeCode } from "../../utils/transformTraveler";
import type { TravellerDeselectedEventData } from "../../types/types";
import { travellersQueryOptions } from "../../hooks/useTravellers";

type Props = PropsWithChildren<{
  initialTravellersSlots?: TravellerSlots;
  travelDate: Date;
}>;

export const TravellersSelectionProvider = ({
  initialTravellersSlots,
  travelDate,
  children,
}: Props) => {
  const { data: travellers } = useQuery(travellersQueryOptions());

  const [travellerSlots, setTravellerSlots] = useState<TravellerSlots>(
    initialTravellersSlots || { ADT: [], CHD: [], INF: [] },
  );

  useEffect(() => {
    const handleTravellerUpdated: WindowEventHandler<"travellerupdated"> = (
      event,
    ) => {
      if (
        getTravellerTypeAtTravelDate(
          event.detail.travellerOldData.birthDate,
          travelDate,
        ) ===
        getTravellerTypeAtTravelDate(
          event.detail.travellerNewData.birthDate,
          travelDate,
        )
      ) {
        return;
      }

      // if the age group of the traveller has changed, unselect the traveller
      // to prevent issues with service selection
      setTravellerSlots((prev) =>
        mapValues(prev, (ageGroup) =>
          ageGroup.map((travellerId) =>
            travellerId === event.detail.travellerNewData.id
              ? null
              : travellerId,
          ),
        ),
      );
    };

    const handleTravellerRemoved: WindowEventHandler<"travellerremoved"> = (
      event,
    ) => {
      setTravellerSlots((prev) =>
        mapValues(prev, (ageGroup) =>
          ageGroup.map((travellerId) =>
            travellerId === event.detail.travellerId ? null : travellerId,
          ),
        ),
      );
    };

    window.addEventListener("travellerupdated", handleTravellerUpdated);
    window.addEventListener("travellerremoved", handleTravellerRemoved);

    return () => {
      window.removeEventListener("travellerupdated", handleTravellerUpdated);
      window.removeEventListener("travellerremoved", handleTravellerRemoved);
    };
  }, [travelDate]);

  const selectTraveller = useCallback<
    TravellersSelectionContextValue["selectTraveller"]
  >(
    (slotIndex, travellerId) => {
      const traveller = travellers?.find((t) => t.id === travellerId);
      if (!traveller) {
        return;
      }

      const travellerAgeGroup = travelerTypeToTypeCode(
        getTravellerTypeAtTravelDate(traveller.birthDate, travelDate),
      );

      setTravellerSlots((prev) => {
        return mapValues(prev, (travellersIds, ageGroup) => {
          if (travellerAgeGroup !== ageGroup) {
            return travellersIds;
          }

          let newTravellersIds = [...travellersIds];

          if (newTravellersIds.includes(travellerId)) {
            const event = new CustomEvent<TravellerDeselectedEventData>(
              "travellerdeselected",
              {
                detail: { travellerId },
              },
            );

            window.dispatchEvent(event);

            newTravellersIds = newTravellersIds.map((id) =>
              id === travellerId ? null : id,
            );
          }

          newTravellersIds[slotIndex] = travellerId;
          return newTravellersIds;
        });
      });
    },
    [travelDate, travellers],
  );

  const getTravellerIndex = useCallback<
    TravellersSelectionContextValue["getTravellerIndex"]
  >(
    (travellerId) => {
      const ageGroupArrays = Object.values(travellerSlots);

      for (const ageGroupArray of ageGroupArrays) {
        const travellerIndex = ageGroupArray.findIndex(
          (id) => id === travellerId,
        );

        if (travellerIndex !== -1) {
          return travellerIndex;
        }
      }

      return null;
    },
    [travellerSlots],
  );

  const selectedTravellers = useMemo(() => {
    const selectedTravellersIds = Object.values(travellerSlots)
      .flat()
      .filter((x): x is NonNullable<typeof x> => Boolean(x));

    return (
      travellers?.filter((traveller) =>
        selectedTravellersIds.includes(traveller.id),
      ) || []
    );
  }, [travellers, travellerSlots]);

  const contextValue = useMemo(
    () => ({
      travellerSlots,
      selectedTravellers,
      selectTraveller,
      getTravellerIndex,
    }),
    [travellerSlots, selectedTravellers, getTravellerIndex, selectTraveller],
  );

  return (
    <TravellersSelectionContext value={contextValue}>
      {children}
    </TravellersSelectionContext>
  );
};
